// Opening the cover picker on the covers that are actually relevant.
//
// ─── WHY ────────────────────────────────────────────────────────────────────
//
// 2026-08-17. An album titled "Cadeau pour sa femme / Couple" sent the client
// to a catalogue of 217 travel covers sorted by popularity — Agadir, Italie,
// Korea, Iceland — and left him to find the five wedding covers himself. The
// owner: "the client doesn't need to see other covers and search for love
// couple covers."
//
// We already know what the album is about: the customer told the assistant,
// and it is on `orders.destination_1`. This turns that sentence into the
// category the picker should open on.
//
// ⚠️ NULL IS A FIRST-CLASS ANSWER. An unrecognised occasion opens on "Toutes",
// which is exactly today's behaviour — no worse. Guessing a category wrongly
// is worse than not guessing: it HIDES 200 covers from someone who wanted one
// of them, and the filter is not obviously the reason.
//
// ⚠️ DESTINATIONS ARE NOT LISTED HERE. There are hundreds of place names and
// no list would keep up. A travel album is recognised by the ABSENCE of every
// other occasion, which is also why `voyage` is checked last — "voyage à
// Marrakech pour notre mariage" is a wedding album, and the narrower reading
// has to win.

/** The `covers.categories` values that actually exist in production. */
export type CoverCategory =
  | 'voyage' | 'mariage' | 'anniversaire' | 'famille'
  | 'naissance' | 'religieux' | 'etudes' | 'maroc' | 'memories';

/** Strip accents and punctuation so "Mariage", "mariage" and "3ers" compare
 *  against one another the way a customer types them. */
function flat(s: string | null | undefined): string {
  return String(s ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Occasion words, in the order they are tested. NARROWEST FIRST.
 *
 * Darija sits beside French in every row because that is how the answers
 * arrive — "3ers" and "mariage" are the same album, and the assistant asks the
 * question in Darija.
 */
const RULES: Array<{ category: CoverCategory; re: RegExp }> = [
  // Wedding / couple / love. The row that started this.
  { category: 'mariage', re: /\b(mariage|marriage|3ers|3ars|ars|noce|wedding|couple|amour|love|romantique|fiancailles|khotoba|kh?toba|femme|mari|epoux|epouse|zawaj|zwaj|valentin)\b/ },
  { category: 'naissance', re: /\b(naissance|bebe|baby|nouveau ne|newborn|3a9i9a|a9i9a|aqiqa|baptem|wlada|mouloud)\b/ },
  { category: 'anniversaire', re: /\b(anniversaire|birthday|3id milad|milad|bougie|ans)\b/ },
  { category: 'etudes', re: /\b(etudes|diplome|graduation|remise des diplomes|bac|licence|master|doctorat|ecole|universite|promo)\b/ },
  { category: 'religieux', re: /\b(religieux|omra|umra|hajj|hadj|mecque|makka|madina|pelerinage|3omra|khtana|circoncision)\b/ },
  { category: 'famille', re: /\b(famille|family|3ayla|aila|parents|enfants|wladi|dar)\b/ },
  { category: 'maroc', re: /\b(maroc|morocco|maghrib|marocain)\b/ },
  // LAST on purpose: "voyage à Marrakech pour notre mariage" is a wedding
  // album. Every narrower reading has already had its turn.
  { category: 'voyage', re: /\b(voyage|trip|travel|vacances|holiday|safar|tourisme|sejour)\b/ },
];

/**
 * Which category should the picker open on for this album?
 *
 * `null` means "we do not know" — the picker opens on Toutes, unchanged.
 */
export function coverCategoryFor(destination: string | null | undefined): CoverCategory | null {
  const t = flat(destination);
  if (!t) return null;
  for (const { category, re } of RULES) if (re.test(t)) return category;
  return null;
}
